<template>
  <div class="Onboarding">
    <div class="Onboarding-aside d-none d-lg-block">
      <div class="Onboarding-bg">
        <div class="Onboarding-aside-logoContainer">
          <img :src="require(`@/assets/logo-${$site}.png`)" />
        </div>
        <div class="Onboarding-aside-infosContainer">
          <h1>{{ $t('onBoarding.aside.title') }}</h1>
          <p>{{ $t('onBoarding.aside.subtitle') }}</p>
        </div>
      </div>
    </div>
    <div class="Onboarding-main pa-4 pa-md-10">
      <div class="Main-loader">
        <div class="Loader-pre"></div>
        <v-progress-linear
          :value="setLoader"
          class="Loader-progressbar"
          color="rgba(61, 66, 77, 0.9)" 
          data-e2e="OnboardingProgressbar"
        />
      </div>
      <div class="Main-section pa-4 pa-sm-10">
        <Step-contact
          v-if="currentStep === 0"
          :contact="contact"
          @nextStep="defineStep"
          data-e2e="OnboardingStepContact"
        />
        <Step-schedule
          v-if="currentStep === 1"
          :error="error"
          :schedules="schedules"
          @nextStep="defineStep"
          data-e2e="OnboardingStepSchedule"
        />
        <Step-service
          v-if="currentStep === 2"
          :error="error"
          :services="availableServices"
          @nextStep="defineStep"
          data-e2e="OnboardingStepService"
        />
        <Step-appointment
          v-if="currentStep === 3"
          :error="error"
          @error="error = $event"
          :schedules="schedules"
          :services="services"
          @nextStep="defineStep"
          data-e2e="OnboardingStepAppointment"
        />
      </div>
    </div>
  </div>
</template>

<script>
import StepContact from '@/components/onboarding/StepContact'
import StepSchedule from '@/components/onboarding/StepSchedule'
import StepService from '@/components/onboarding/StepService'
import StepAppointment from '@/components/onboarding/StepAppointment'
import { dayOfWeek } from '@/const/shared'
import gql from 'graphql-tag'
import { mapGetters } from 'vuex'
import { convertHoursToMinutes, formatHour, formatHourToFloat } from '@/services/time'
import { sorter } from '@/services/sorter'

// region GQL Requests
const INIT_SCHEDULE = gql`
  mutation initShopOpenings(
    $shopUuid: GUID!
    $maxParallelAppointments: MaxParallelAppointmentsInput
    $openings: [ShopOpeningInput!]!
  ) {
    initShopOpenings(shopUuid: $shopUuid, maxParallelAppointments: $maxParallelAppointments, openings: $openings) {
      shopUuid
      country
    }
  }
`
const SELECT_SHOP_SERVICES = gql`
  mutation selectShopServices($shopUuid: GUID!, $services: [ServiceType!]!) {
    selectShopServices(shopUuid: $shopUuid, services: $services) {
      shopUuid
      services {
        duration
        enabled
        openings {
          beginHour
          dayOfWeek
          endHour
        }
        serviceType
        serviceUuid
      }
    }
  }
`
const SETUP_SHOP_SERVICES = gql`
  mutation setupShopServices($shopUuid: GUID!, $services: [ServiceSetupInput!]!) {
    setupShopServices(shopUuid: $shopUuid, services: $services) {
      isOnboarded
      services {
        duration
        enabled
        openings {
          beginHour
          dayOfWeek
          endHour
        }
        parallelAppointment
        price
        serviceType
        serviceUuid
      }
    }
  }
`

const GET_SERVICE_TYPES = gql`
  query serviceTypes($country: String!) {
    serviceTypes(country: $country)
  }
`
//endregion

export default {
  name: 'Onboarding',
  components: {
    StepContact,
    StepSchedule,
    StepService,
    StepAppointment
  },
  data() {
    return {
      availableServices: [],
      currentStep: 0,
      contact: {
        email: this.$store.getters.currentShop.email,
        location: `${this.$store.getters.currentShop.address1} ${this.$store.getters.currentShop.postalCode} ${this.$store.getters.currentShop.city}`
      },
      country: null,
      error: null,
      schedules: [],
      services: []
    }
  },

  apollo: {
    availableServices: {
      query: GET_SERVICE_TYPES,
      skip() {
        return !this.$get(this.$store.getters, 'currentShop.country')
      },
      update(data) {
        return data.serviceTypes.map(type => ({ key: type, icon: this.defineServiceIcon(type), active: false }))
      },
      variables() {
        return { country: this.$store.getters.currentShop.country }
      }
    }
  },

  created() {
    // Define step in case of a resuming
    const obData = this.$store.getters.currentShop
    let currentStep = 0
    if (obData.openings.length === 0) {
      this.schedules = this.defaultSchedules
      this.services = this.availableServices
    } else if (obData.services.length === 0) {
      this.schedules = this.formatExistingSchedule(obData.openings)
      this.services = this.availableServices
      currentStep = 2
    } else {
      this.schedules = this.formatExistingSchedule(obData.openings)
      this.services = this.formatExistingServices(obData.services)
      currentStep = 3
    }
    this.currentStep = currentStep
  },
  computed: {
    ...mapGetters(['currentShop']),
    setLoader() {
      if (this.currentStep === 1) {
        return 33.33
      } else if (this.currentStep === 2) {
        return 66.66
      } else if (this.currentStep === 3) {
        return 100
      } else {
        return 0
      }
    },
    defaultSchedules() {
      return [
        { key: dayOfWeek.MONDAY, start: 8, end: 19, active: false, maxSlot: 1 },
        { key: dayOfWeek.TUESDAY, start: 8, end: 19, active: false, maxSlot: 1 },
        { key: dayOfWeek.WEDNESDAY, start: 8, end: 19, active: false, maxSlot: 1 },
        { key: dayOfWeek.THURSDAY, start: 8, end: 19, active: false, maxSlot: 1 },
        { key: dayOfWeek.FRIDAY, start: 8, end: 19, active: false, maxSlot: 1 },
        { key: dayOfWeek.SATURDAY, start: 8, end: 19, active: false, maxSlot: 1 },
        { key: dayOfWeek.SUNDAY, start: 8, end: 19, active: false, maxSlot: 1 }
      ]
    }
  },
  methods: {
    async defineStep(step) {
      let mutation, mutationName
      let variablesGQL = { shopUuid: this.$store.getters.currentShop.shopUuid }
      let variablesStore = {
        shopUuid: this.$store.getters.currentShop.shopUuid,
        data: []
      }
      let isMutation = false

      switch (step) {
        case 1:
          // Not used atm
          break
        case 2:
          isMutation = true
          mutation = INIT_SCHEDULE
          mutationName = 'INIT_SCHEDULE'
          variablesGQL.openings = this.extractDataGQL(this.formatScheduleEntry, this.schedules)
          variablesGQL.maxParallelAppointments = this.schedules.reduce(
            (o, schedule) => ({
              ...o,
              [schedule.key]: schedule.maxSlot
            }),
            {}
          )

          variablesStore.maxParallelAppointments = variablesGQL.maxParallelAppointments

          for (let item of variablesGQL.openings) {
            variablesStore.data.push({
              ...item,
              shopUuid: this.$store.getters.currentShop.shopUuid,
              __typename: 'ShopOpening'
            })
          }
          break
        case 3:
          isMutation = true
          mutation = SELECT_SHOP_SERVICES
          mutationName = 'SELECT_SHOP_SERVICES'
          variablesGQL.services = this.extractDataGQL(this.formatServiceEntry, this.availableServices)
          break
        case 4:
          isMutation = true
          mutation = SETUP_SHOP_SERVICES
          mutationName = 'SETUP_SHOP_SERVICES'
          variablesGQL.services = this.formatOpeningsServiceEntry(this.services)
          break
        default:
          break
      }

      // Process mutations
      if (isMutation) {
        try {
          const { data } = await this.$apollo.mutate({
            mutation,
            variables: variablesGQL
          })

          if (step === 2) {
            this.country = data.initShopOpenings.country
          }

          if (step === 3) {
            this.services = this.formatExistingServices(data.selectShopServices.services)
          }
          // const payload = data
          const payload = variablesStore.data.length > 0 ? variablesStore : data
          this.$store.commit(mutationName, payload)
        } catch (error) {
          this.error = error.message.replace(/GraphQL error: +/, '')
        }
      }

      // Assign step + Redirect if needed
      if (step === 4) {
        await this.$router.push({ name: 'Calendar' })
      } else if (!this.error) {
        this.currentStep = step
      }
    },
    extractDataGQL(formatMethod, source) {
      return source.filter(el => el.active).map(el => formatMethod(el))
    },
    formatExistingSchedule(openings) {
      let newSchedule = []

      // Extract existing datas
      for (const opening of openings) {
        const start = formatHourToFloat(opening.beginHour)
        const end = formatHourToFloat(opening.endHour)

        // Check overlapping values
        {
          const overlap = newSchedule.find(
            s => s.key === opening.dayOfWeek && s.end <= opening.start && s.start >= opening.end
          )
          if (overlap) {
            overlap.start = Math.min(overlap.start, opening.start)
            overlap.end = Math.min(overlap.end, opening.end)
            continue
          }
        }

        newSchedule.push({
          key: opening.dayOfWeek,
          start,
          end,
          active: true,
          maxSlot: this.$store.getters.currentShop.maxParallelAppointments[opening.dayOfWeek] || 1
        })
      }

      // Check presence of all days
      const templateDays = [
        { key: dayOfWeek.MONDAY, found: false },
        { key: dayOfWeek.TUESDAY, found: false },
        { key: dayOfWeek.WEDNESDAY, found: false },
        { key: dayOfWeek.THURSDAY, found: false },
        { key: dayOfWeek.FRIDAY, found: false },
        { key: dayOfWeek.SATURDAY, found: false },
        { key: dayOfWeek.SUNDAY, found: false }
      ]
      for (let item of newSchedule) {
        const itemFound = templateDays.find(el => el.key === item.key)
        itemFound.found = true
      }
      for (let item of templateDays) {
        !item.found &&
          newSchedule.push({
            key: item.key,
            start: 9,
            end: 19,
            active: false
          })
      }

      // Sort && return
      newSchedule = sorter(newSchedule, 'days')
      return newSchedule
    },
    formatExistingServices(services) {
      const newServices = services
        .map(service => {
          return {
            duration: 0.25,
            enabled: service.active === undefined ? true : service.active,
            icon: this.defineServiceIcon(service.serviceType),
            key: service.serviceType || service.key,
            openings: this.schedules.map(({ key, start, end, ...rest }) => ({
              dayOfWeek: key,
              beginHour: start,
              endHour: end,
              ...rest
            })),
            parallelAppointment: 1,
            serviceUuid: service.serviceUuid
          }
        })
        .filter(service => service.enabled)

      return sorter(newServices, 'service')
    },
    formatOpeningsServiceEntry(services) {
      return services.map(service => ({
        duration: (service.duration && convertHoursToMinutes(service.duration)) || 15,
        openings: service.openings
          .filter(opening => opening.active !== false)
          .map(opening => ({
            dayOfWeek: opening.dayOfWeek,
            beginHour:
              typeof opening.beginHour === 'string' ? opening.beginHour : formatHour(opening.beginHour).display,
            endHour: typeof opening.endHour === 'string' ? opening.endHour : formatHour(opening.endHour).display
          })),
        parallelAppointment: service.parallelAppointment,
        price: service.price,
        serviceUuid: service.serviceUuid
      }))
    },
    formatScheduleEntry(data) {
      data.start = this.formatHourGQL(data.start)
      data.end = this.formatHourGQL(data.end)
      return {
        dayOfWeek: data.key,
        beginHour: data.start,
        endHour: data.end
      }
    },
    formatServiceEntry(data) {
      return data.key
    },
    formatHourGQL(hour) {
      let floatHour = hour - Math.floor(hour)
      hour = ('0' + Math.floor(hour)).slice(-2)
      floatHour === 0.5 ? (hour += ':30') : (hour += ':00')
      return hour
    },
    defineServiceIcon(key) {
      const service = this.$const.serviceTypeValues.find(el => el.value === key)
      return service ? service.icon : ''
    }
  }
}
</script>

<style scoped lang="scss">
.Onboarding {
  height: 100%;
  display: flex;
}

.Onboarding-aside {
  background-image: url('../assets/sidebar-background-essilor.jpg');
  background-size: cover;
  background-position: center;
  height: 100%;
  width: 20%;
  max-width: 315px;
  .Onboarding-bg {
    background-color: rgba(69, 76, 94, 0.9);
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    .Onboarding-aside-logoContainer {
      position: absolute;
      top: 0;
      left: 0;
      margin: 15px;
    }
    .Onboarding-aside-infosContainer {
      margin-bottom: 150px;
      * {
        color: #fff;
        margin: 15px 30px;
      }
    }
  }
}

.Onboarding-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-x: auto;
  .Main-loader {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-top: 20px;
    margin-bottom: 50px;
    .Loader-pre {
      background-color: var(--v-primary-base);
      border-radius: 20px;
      height: 20px;
      width: 20px;
      margin-right: 15px;
    }
    .Loader-progressbar {
      border-radius: 20px;
      height: 20px !important;
    }
  }
  .Main-section {
    border: 2px solid var(--v-secondary-lighten1);
    border-radius: 3px;
    flex: 1;
  }
}
</style>

<style lang="scss">
.App-galileo .Onboarding-aside {
  background-image: url('../assets/sidebar-background-galileo.jpg');
  .Onboarding-bg {
    background-color: rgba(0, 163, 110, 0.85);
  }
}

.App-nikon-it .Onboarding-aside {
  background-image: url('../assets/sidebar-background-nikon-it.jpg');
  .Onboarding-bg {
    background-color: rgba(0, 0, 0, 0.85);
  }
}
</style>
